'use client';

import React, { useMemo } from 'react';
import StreamingMessage from './StreamingMessage';
import QuestionButtons from './QuestionButtons';

interface MessageContentWithButtonsProps {
  content: string;
  isComplete: boolean;
  buttons: string[];
  buttonPlaceholder: string | null;
  onButtonClick?: (buttonText: string) => void;
}

const MessageContentWithButtons: React.FC<MessageContentWithButtonsProps> = ({
  content,
  isComplete,
  buttons,
  buttonPlaceholder,
  onButtonClick,
}) => {
  // Memoize the parts array to avoid recalculating on every render
  const parts = useMemo(() => {
    if (!buttonPlaceholder) return null;
    return content.split(buttonPlaceholder);
  }, [content, buttonPlaceholder]);

  // If no button placeholder, render content normally
  if (!buttonPlaceholder || !parts) {
    return <StreamingMessage content={content} isComplete={isComplete} />;
  }

  return (
    <>
      {parts.map((part, index) => {
        const isLastPart = index === parts.length - 1;

        return (
          <React.Fragment key={`part-${index}`}>
            {/* Render text part if it exists */}
            {part && (
              <StreamingMessage
                content={part}
                isComplete={isComplete || !isLastPart}
              />
            )}
            {/* Render buttons between parts (not after the last part) */}
            {index < parts.length - 1 && buttons.length > 0 && onButtonClick && (
              <div className="my-4">
                <QuestionButtons
                  buttons={buttons}
                  onButtonClick={onButtonClick}
                  disabled={!isComplete}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default React.memo(MessageContentWithButtons);
